import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2;
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { DataGridColumn } from "abreact/incubator/ui/datagrid/DataGridColumn";
import { BaseMachine } from "abreact/incubator/ui/machine/BaseMachine";
import { AbWebServices } from "abweb/service/AbWebServices";
import { observable, runInAction, makeObservable } from "mobx";
export let LoginHistoryColumns = /*#__PURE__*/function (LoginHistoryColumns) {
  LoginHistoryColumns[LoginHistoryColumns["LOGIN"] = 0] = "LOGIN";
  LoginHistoryColumns[LoginHistoryColumns["LOGOUT"] = 1] = "LOGOUT";
  LoginHistoryColumns[LoginHistoryColumns["DURATION"] = 2] = "DURATION";
  LoginHistoryColumns[LoginHistoryColumns["LOCATION"] = 3] = "LOCATION";
  LoginHistoryColumns[LoginHistoryColumns["CONCURRENT"] = 4] = "CONCURRENT";
  return LoginHistoryColumns;
}({});
export let LoginHistoryMachine = (_class = class LoginHistoryMachine extends BaseMachine {
  constructor() {
    super();
    _defineProperty(this, "_loginSessionInfos", null);
    _initializerDefineProperty(this, "_loaded", _descriptor, this);
    _defineProperty(this, "_currentSessionIndex", null);
    _defineProperty(this, "columns", [createColumn(LoginHistoryColumns.LOGIN, 240), createColumn(LoginHistoryColumns.LOGOUT, 240), createColumn(LoginHistoryColumns.DURATION, 120), createColumn(LoginHistoryColumns.LOCATION, 120), createColumn(LoginHistoryColumns.CONCURRENT, 28)]);
    /**
     * Date formatter which will be used by {@linkcode formatDate}
     * @type {Intl.DateTimeFormat}
     * @private
     */
    _defineProperty(this, "_formatter", new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      timeZoneName: "short"
    }));
    _initializerDefineProperty(this, "selectedIndex", _descriptor2, this);
    makeObservable(this);
    this._loaded = null;
    this.selectedIndex = null;
    this.loadHistory();
  }
  get loaded() {
    return this._loaded;
  }
  get currentSessionIndex() {
    return this._currentSessionIndex;
  }
  get loginSessionInfos() {
    return this._loginSessionInfos;
  }
  isSessionConcurrent(index) {
    if (this.selectedIndex !== null) {
      if (index === this.selectedIndex) {
        return true;
      }
      if (this._loginSessionInfos !== null) {
        const toInterval = s => [s.loginTime.getTime(), s.logoutTime.getTime()];
        const argInterval = toInterval(this._loginSessionInfos[index]);
        const selectedInterval = toInterval(this._loginSessionInfos[this.selectedIndex]);
        if (selectedInterval[1] === 0)
          // selected session is still logged in
          {
            return argInterval[1] === 0
            // arg session still logged in
            || argInterval[1] > selectedInterval[0];
            // arg session ended after selected session started
          } else
          // selected session did end
          {
            return selectedInterval[0] <= argInterval[0] && argInterval[0] < selectedInterval[1]
            // |------|       selected
            //    |->         arg
            || argInterval[0] < selectedInterval[0] && (argInterval[1] === 0 || argInterval[1] > selectedInterval[0]);
            //    |------|    selected
            // |---->         arg
          }
      }
    }
    return false;
  }
  isSessionActive(index) {
    return this._currentSessionIndex !== null && index === this._currentSessionIndex || false;
  }
  getStartTimeText(sessionInfo) {
    return this.formatDate(sessionInfo.loginTime);
  }
  getEndTimeText(sessionInfo) {
    return sessionInfo.logoutTime.getTime() === 0 ? AbReactResources.loginHistory_dialog_row_label_loggedIn() : this.formatDate(sessionInfo.logoutTime);
  }
  getDurationText(sessionInfo) {
    const interval = sessionInfo.logoutTime.getTime() - sessionInfo.loginTime.getTime();
    if (interval < 0) {
      return "";
    }
    const seconds = Math.floor(interval / 1000) % 60;
    const minutes = Math.floor(interval / 60000) % 60;
    const hours = Math.floor(interval / 3600000);
    const normalize = n => (n < 10 ? "0" : "") + n;
    return `${hours}:${normalize(minutes)}:${normalize(seconds)}`;
  }
  getLocationText(sessionInfo) {
    return sessionInfo.location;
  }
  async loadHistory() {
    const history = await AbWebServices.authenticationService.getLoginHistory();
    runInAction(() => {
      if (history === null) {
        this._loaded = false;
        return;
      }
      // TODO turn into an index on the JAVA side
      if (history.hasCurrentSession) {
        this._currentSessionIndex = history.currentUserSession;
        this.selectedIndex = history.currentUserSession;
      }
      this._loginSessionInfos = history.sessions;
      this._loaded = true;
    });
  }
  getGridSessionsAsTabbedString() {
    const header = AbReactResources.loginHistory_copy_header();
    const lines = this.loginSessionInfos ? this.loginSessionInfos.map(info => `${this.getStartTimeText(info)}` + `\t${this.getEndTimeText(info)}` + `\t${this.getDurationText(info)}` + `\t${this.getLocationText(info)}`).join("\n") : "";
    return `${header}\n${lines}`;
  }

  /**
   * Formats a date in the `Date.toUTCString()` style, but in the local timezone. There's really no way to do that built into the
   * `Date` class, which is really stupid.
   *
   * @param date  the date to format
   * @return the string in the format of `toUTCString()`, e.g. "Thu, 26 May 2022 9:42:35 EDT"
   */
  formatDate(date) {
    const parts = new Map();
    this._formatter.formatToParts(date) // Extract the parts
    .filter(part => part.type !== "literal") // Get rid of the literals, like commas
    .forEach(part => parts.set(part.type, part.value)); // Populate the map

    // Put together the date string
    return `${parts.get("weekday")}, ${parts.get("day")} ${parts.get("month")} ${parts.get("year")} ${parts.get("hour")}:${parts.get("minute")}:${parts.get("second")} ${parts.get("timeZoneName")}`;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_loaded", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "selectedIndex", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
function createColumn(column, width) {
  const col = new DataGridColumn(LoginHistoryColumns[column]);
  col.allowResize = false;
  col.editable = false;
  if (column !== LoginHistoryColumns.CONCURRENT) {
    col.label = AbWebServices.i18n.getMessage("abreact", `loginHistory.dialog.column.label.${LoginHistoryColumns[column]}`);
  }
  col.allowSort = false;
  col.width = width;
  col.minWidth = width;
  return col;
}
//# sourceMappingURL=LoginHistoryMachine.js.map
